import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DollarSign, User, Link as LinkIcon, Phone, Gift, StickyNote, Calendar } from "lucide-react";
import { dateKey } from "@/lib/format";
import { toast } from "sonner";

const Field = ({ icon: Icon, label, children }) => (
  <label className="block">
    <span className="text-[10px] font-semibold uppercase tracking-widest text-white/55 flex items-center gap-1.5 mb-1.5">
      <Icon className="w-3.5 h-3.5" strokeWidth={2.2} /> {label}
    </span>
    {children}
  </label>
);

export function AddSaleDialog({ open, onClose, onSave, initial }) {
  const [form, setForm] = useState({
    customer_name: "",
    salesforce_url: "",
    phone: "",
    amount: "",
    bonus: "",
    sale_date: dateKey(new Date()),
    notes: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (initial) {
      setForm({
        customer_name: initial.customer_name || "",
        salesforce_url: initial.salesforce_url || "",
        phone: initial.phone || "",
        amount: String(initial.amount ?? ""),
        bonus: String(initial.bonus ?? ""),
        sale_date: initial.sale_date || dateKey(new Date()),
        notes: initial.notes || "",
      });
    } else {
      setForm((f) => ({ ...f, customer_name: "", salesforce_url: "", phone: "", amount: "", bonus: "", notes: "" }));
    }
  }, [initial, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim()) return toast.error("Customer name required");
    if (!form.amount || isNaN(Number(form.amount))) return toast.error("Valid sale amount required");
    setBusy(true);
    try {
      await onSave({
        customer_name: form.customer_name.trim(),
        salesforce_url: form.salesforce_url.trim(),
        phone: form.phone.trim(),
        amount: Number(form.amount),
        bonus: Number(form.bonus || 0),
        sale_date: form.sale_date,
        notes: form.notes.trim(),
      });
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-md p-0 gap-0 rounded-3xl overflow-hidden bg-slate-900/85 backdrop-blur-2xl border border-white/20 text-white"
        data-testid="add-sale-dialog"
      >
        <DialogHeader className="p-6 pb-2">
          <DialogTitle className="font-heading text-xl font-bold text-white">
            {initial ? "Edit Sale" : "New Sale"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="p-6 pt-2 space-y-4">
          <Field icon={User} label="Customer Name">
            <input
              className="glass-input w-full rounded-xl px-3 py-2.5 text-sm"
              value={form.customer_name}
              onChange={(e) => set("customer_name", e.target.value)}
              placeholder="Jane Doe"
              data-testid="input-customer-name"
              autoFocus
            />
          </Field>
          <Field icon={LinkIcon} label="Salesforce URL">
            <input
              className="glass-input w-full rounded-xl px-3 py-2.5 text-sm"
              value={form.salesforce_url}
              onChange={(e) => set("salesforce_url", e.target.value)}
              placeholder="https://your-org.lightning.force.com/…"
              data-testid="input-salesforce-url"
            />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field icon={Phone} label="Phone">
              <input
                className="glass-input w-full rounded-xl px-3 py-2.5 text-sm"
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                placeholder="(555) 123-4567"
                data-testid="input-phone"
              />
            </Field>
            <Field icon={Calendar} label="Date">
              <input
                type="date"
                className="glass-input w-full rounded-xl px-3 py-2.5 text-sm"
                value={form.sale_date}
                onChange={(e) => set("sale_date", e.target.value)}
                data-testid="input-sale-date"
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field icon={DollarSign} label="Sale Amount">
              <input
                type="number"
                step="0.01"
                className="glass-input w-full rounded-xl px-3 py-2.5 text-sm"
                value={form.amount}
                onChange={(e) => set("amount", e.target.value)}
                placeholder="5000"
                data-testid="input-sale-amount"
              />
            </Field>
            <Field icon={Gift} label="Bonus (optional)">
              <input
                type="number"
                step="0.01"
                className="glass-input w-full rounded-xl px-3 py-2.5 text-sm"
                value={form.bonus}
                onChange={(e) => set("bonus", e.target.value)}
                placeholder="0"
                data-testid="input-bonus"
              />
            </Field>
          </div>
          <Field icon={StickyNote} label="Notes">
            <textarea
              rows={2}
              className="glass-input w-full rounded-xl px-3 py-2.5 text-sm resize-none"
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="Anything to remember…"
              data-testid="input-notes"
            />
          </Field>
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-full glass-pill text-sm font-semibold text-white active:opacity-70"
              data-testid="cancel-sale-btn"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="flex-1 py-2.5 rounded-full bg-white text-slate-900 font-semibold active:opacity-80 disabled:opacity-60"
              data-testid="save-sale-btn"
            >
              {busy ? "Saving…" : initial ? "Save Changes" : "Add Sale"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
